import type { Board } from "../board";
import type { Direction } from "../direction";
import { BallisticMovementEffect } from "../effects/ballisticMovementEffect";
import { BigShockBlast } from "../effects/bigShockBlast";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../effects/movementEffect";
import { ShockBlast } from "../effects/shockBlast";
import { Mob } from "../mob";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import type { Player } from "../player";
import { ArtilleryRobot } from "../things/artilleryRobot";
import { BigContainer } from "../things/bigContainer";
import { GoldDrop } from "../things/goldDrop";
import { LevelChangerThing } from "../things/levelChangerThing";
import { LootBox } from "../things/lootBox";
import { MiningRobot } from "../things/miningRobot";
import { Portal2 } from "../things/portal2/portal2";
import { RobotSpawner } from "../things/robotSpawner";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import type { MultiTileThingCore } from "../things/multitile/multiTileThingCore";
import { Weapon } from "../weapon";
import { XY } from "../xy";

export class OpGun2 extends Weapon {
	static key = "OpGun2";
	constructor(owner: Player) {
		super(owner, 0, true);
	}

	fire(_direction: Direction, board: Board): void {
		board.addNonPhysicalThing(new Sound(SoundId.LASER_BEAM));
		const ownerXY = this.owner.xy;
		board.addEffect(new BigShockBlast(ownerXY));

		// Kill all mobs
		const mobs = board.getPhysicalThings().filter((t) => t instanceof Mob && t.active);
		for (const mob of mobs) {
			if (!mob.active) continue;
			board.addEffect(new ShockBlast(mob.xy));
			(mob as Mob).kill();
		}

		// Kill all multi-tile robots (MiningRobot, ArtilleryRobot)
		const multiTileMobs = board
			.getPhysicalThings()
			.filter((t) => (t instanceof MiningRobot || t instanceof ArtilleryRobot) && t.active);
		for (const r of multiTileMobs) {
			if (!r.active) continue;
			const robot = r as MiningRobot | ArtilleryRobot;
			board.addEffect(new ShockBlast(robot.xy));
			robot.applyDamage(null, robot.hp, null);
		}

		// Kill all portals and spawners
		const portals = board
			.getPhysicalThings()
			.filter((t) => (t instanceof Portal2 || t instanceof RobotSpawner) && t.active);
		for (const p of portals) {
			if (!p.active) continue;
			board.addEffect(new ShockBlast(p.xy));
			(p as PhysicalThingWithHp).kill();
		}

		// Open BigContainers
		const containers = board.getPhysicalThings().filter((t) => t instanceof BigContainer && t.active);
		const seen = new Set<MultiTileThingCore>();
		for (const c of containers) {
			const bc = c as BigContainer;
			if (seen.has(bc)) continue;
			seen.add(bc);
			bc.open();
		}

		// Find nearest level changer
		const levelChangers = board.getPhysicalThings().filter((t) => t instanceof LevelChangerThing);
		if (levelChangers.length === 0) return;

		// Sort by distance (farthest first per Java code: -distanceSquared)
		levelChangers.sort((a, b) => b.xy.distanceSquared(this.owner.xy) - a.xy.distanceSquared(this.owner.xy));
		const target = levelChangers[0];

		// Find passable neighbor of target
		const targetXY = target.xy.getNeighbors().find((n) => board.isInBounds(n) && board.isPassable(n));
		if (!targetXY) return;

		// Move loot boxes to target
		const lootBoxes = board.getPhysicalThings().filter((t) => t instanceof LootBox && t.active);
		for (const lb of lootBoxes) {
			const lbOldXY = lb.xy;
			board.moveThing(lb, targetXY);
			if (!lbOldXY.equals(targetXY)) {
				const effect = new BallisticMovementEffect(lb, lbOldXY, targetXY);
				effect.height = 2;
				board.addEffect(effect);
			}
		}

		// Move gold drops to target — player.grabNearbyGold will collect them after the move
		const goldDrops = board.getPhysicalThings().filter((t) => t instanceof GoldDrop && t.active);
		for (const gd of goldDrops) {
			const gdOldXY = gd.xy;
			board.moveThing(gd, targetXY);
			if (!gdOldXY.equals(targetXY)) {
				const effect = new BallisticMovementEffect(gd, gdOldXY, targetXY);
				effect.height = 2;
				board.addEffect(effect);
			}
		}

		// Move player to target
		if (board.player) {
			const playerOldXY = board.player.xy;
			board.moveThing(board.player, targetXY);
			if (!playerOldXY.equals(targetXY)) {
				board.addEffect(new MovementEffect(board.player, playerOldXY, targetXY, STANDARD_MOVEMENT_TIME * 5));
				// ShockBlast line from player origin to target
				const dx = targetXY.x - playerOldXY.x;
				const dy = targetXY.y - playerOldXY.y;
				const steps = Math.max(Math.abs(dx), Math.abs(dy));
				if (steps > 0) {
					for (let i = 0; i <= steps; i++) {
						const x = playerOldXY.x + Math.round((dx * i) / steps);
						const y = playerOldXY.y + Math.round((dy * i) / steps);
						const lineXY = new XY(x, y);
						if (board.isInBounds(lineXY)) {
							board.addEffect(new ShockBlast(lineXY));
						}
					}
				}
			}
		}
	}

	static deserialize(_data: any, owner: Player): OpGun2 {
		return new OpGun2(owner);
	}
}
